import { Alert, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../provider/AuthProvider";
import {
  getUsersByEmail,
  loginWithEmail,
  verifyCode,
  verifyLoginMfa,
} from "../services/authService";

export default function VerifyCodePage() {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { setAccessToken } = useAuth();
  const mode = location.state?.mode;
  const email = location.state?.email || "";
  const cisloOdberatele = location.state?.cisloOdberatele;
  const mfaTyp = location.state?.mfaTyp;

  useEffect(() => {
    if (!mode || (mode !== "password-mfa" && !email)) {
      navigate("/login", { replace: true });
    }
  }, [email, mode, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    if (!code.trim()) {
      setError("Vyplňte ověřovací kód.");
      return;
    }

    setIsSubmitting(true);
    try {
      if (mode === "password-mfa") {
        const result = await verifyLoginMfa(code.trim());
        if (!result.success) {
          setError(result.message || "Neplatný nebo expirovaný kód.");
          return;
        }
        setAccessToken(result.token);
        navigate("/zvk", { replace: true });
        return;
      }

      const verified = await verifyCode(email, code.trim());
      if (!verified.success) {
        setError(verified.message || "Neplatný nebo expirovaný kód.");
        return;
      }

      if (mode === "login") {
        const result = await loginWithEmail(email);
        if (!result.success) {
          setError(result.message || "Přihlášení e-mailem se nezdařilo.");
          return;
        }
        if (result.data?.multiple) {
          navigate("/select-user", {
            replace: true,
            state: { users: result.data.users, mode: "login" },
          });
        } else {
          navigate("/email-password", {
            replace: true,
            state: { cisloOdberatele: result.data?.user?.cisloOdberatele },
          });
        }
        return;
      }

      if (mode === "register") {
        navigate("/register", {
          replace: true,
          state: { cisloOdberatele, email, registrationVerified: true },
        });
        return;
      }

      if (mode === "forgot") {
        const result = await getUsersByEmail(email);
        if (!result.success) {
          setError(result.message || "Odběratele se nepodařilo načíst.");
          return;
        }
        if (result.data?.multiple) {
          navigate("/select-user", {
            replace: true,
            state: { users: result.data.users, mode: "forgot", email },
          });
        } else {
          navigate("/new-password", {
            replace: true,
            state: { email, cisloOdberatele: result.data?.cisloOdberatele, isVerified: true },
          });
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const delivery = mode === "password-mfa"
    ? (mfaTyp === "SMS" ? "telefon" : "e-mail")
    : "e-mail";

  return (
    <Box sx={{ maxWidth: 400, mx: "auto", my: 2 }}>
      <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>Ověření kódu</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Ověřovací kód byl odeslán na {delivery}{email ? ` ${email}` : ""}.
      </Alert>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box component="form" onSubmit={handleSubmit}>
        <TextField label="Ověřovací kód" value={code}
          onChange={(event) => setCode(event.target.value)} disabled={isSubmitting}
          fullWidth required autoComplete="one-time-code" />
        <Button type="submit" variant="contained" disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
          sx={{ display: "block", mx: "auto", mt: 2, width: 200 }}>
          {isSubmitting ? "Ověřuji…" : "Ověřit"}
        </Button>
      </Box>
      <Button variant="outlined" sx={{ display: "block", mx: "auto", mt: 1, width: 200 }}
        onClick={() => navigate("/login")}>
        Zpět k přihlášení
      </Button>
    </Box>
  );
}
